/**
 * QbeTrack module - Extracted to break circular dependency:
 * config → queue → dataLayer → config
 *
 * This module contains the global qbeTrack state and accessors.
 */
import type { Meta, QbeTrack, TestDataLayer } from '../types';

/**
 * Hydrated `window.qbeTrack` shape returned by `getQbeTrack`.
 *
 * QAL ensures `meta`, `meta.test`, `tools`, and `test.dataLayer` are always present so callers can
 * inspect runtime metadata and test data-layer state without defensive optional checks.
 */
export type HydratedQbeTrack = QbeTrack & {
  /** Runtime metadata with guaranteed test data-layer holder. */
  meta: Meta & { test: TestDataLayer };
  /** qbeTrack helper functions with config getters installed by QAL config initialisation. */
  tools: NonNullable<QbeTrack['tools']>;
  /** Test data-layer holder used when `unitTestMode` is enabled. */
  test: TestDataLayer;
};

type AnalyticsGlobal = typeof globalThis & {
  qbeTrack?: QbeTrack;
  dataLayer?: Array<object>;
};

const getAnalyticsGlobal = (): AnalyticsGlobal =>
  typeof window === 'undefined'
    ? (globalThis as AnalyticsGlobal)
    : (window as AnalyticsGlobal);

const analyticsGlobal = getAnalyticsGlobal();

const createDefaultTestDataLayer = (): TestDataLayer => ({
  dataLayer: [],
});

const createDefaultMeta = (): Meta => ({
  test: createDefaultTestDataLayer(),
});

const createDefaultTools = (): NonNullable<QbeTrack['tools']> => ({
  getConfigDataPoints: () => ({}),
  getConfig: () => ({}), // Placeholder - will be set by config.ts
});

const hydrateQbeTrack = (): HydratedQbeTrack => {
  const existing = analyticsGlobal.qbeTrack as QbeTrack | undefined;

  const hydrated: QbeTrack = existing ?? {
    meta: createDefaultMeta(),
    tools: createDefaultTools(),
    test: createDefaultTestDataLayer(),
  };

  hydrated.meta = hydrated.meta ?? createDefaultMeta();
  hydrated.meta.test = hydrated.meta.test ?? createDefaultTestDataLayer();
  hydrated.tools = hydrated.tools ?? createDefaultTools();
  hydrated.test = hydrated.test ?? createDefaultTestDataLayer();
  hydrated.test.dataLayer = hydrated.test.dataLayer ?? [];

  analyticsGlobal.qbeTrack = hydrated;

  return hydrated as HydratedQbeTrack;
};

// Initialize qbeTrack singleton
const qbeTrack = hydrateQbeTrack();
qbeTrack.meta.qal = true;

/**
 * Get the global qbeTrack object.
 * Used for testing and debugging utilities.
 *
 * @returns The hydrated global qbeTrack singleton.
 */
export const getQbeTrack = (): HydratedQbeTrack => qbeTrack;

/**
 * Set the getConfig function on qbeTrack.tools.
 * Called by config.ts after config is initialized.
 *
 * @param getConfigFn - Function returning the current QAL runtime config.
 * @param getConfigDataPointsFn - Function returning the public qbeTrack data-points view.
 * @returns Nothing.
 */
export const setQbeTrackConfigGetter = (
  getConfigFn: () => object,
  getConfigDataPointsFn: () => object
): void => {
  qbeTrack.tools.getConfig = getConfigFn;
  qbeTrack.tools.getConfigDataPoints = getConfigDataPointsFn;
};

export { analyticsGlobal };
